/**
 * Vehicle Hooks - React hooks for vehicle data management
 * Epic 34: Járműnyilvántartás (ADR-027)
 */

import {
  createCompanyVehicle,
  createRentalVehicle,
  deleteVehicle,
  getCompanyVehicles,
  getRentalVehicles,
  getVehicleById,
  updateVehicle,
  type CompanyVehicle,
  type CreateCompanyVehicleData,
  type CreateRentalVehicleData,
  type RentalVehicle,
  type Vehicle,
  type VehicleFilters,
} from '@/api/vehicles';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseVehiclesResult<T> {
  vehicles: T[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseVehicleResult {
  vehicle: Vehicle | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseVehicleMutationsResult {
  createRentalVehicle: typeof createRentalVehicle;
  createCompanyVehicle: typeof createCompanyVehicle;
  updateVehicle: typeof updateVehicle;
  deleteVehicle: typeof deleteVehicle;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch and manage rental vehicle list
 */
export function useRentalVehicles(
  filters: Omit<VehicleFilters, 'vehicleType'> = {}
): UseVehiclesResult<RentalVehicle> {
  const [vehicles, setVehicles] = useState<RentalVehicle[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVehicles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getRentalVehicles(filters);
      setVehicles(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni a járműveket';
      setError(message);
      console.error('Error fetching rental vehicles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters.type, filters.status, filters.search, filters.page, filters.pageSize]);

  useEffect(() => {
    void fetchVehicles();
  }, [fetchVehicles]);

  return { vehicles, total, isLoading, error, refetch: fetchVehicles };
}

/**
 * Hook to fetch and manage company vehicle list
 */
export function useCompanyVehicles(
  filters: Omit<VehicleFilters, 'vehicleType'> = {}
): UseVehiclesResult<CompanyVehicle> {
  const [vehicles, setVehicles] = useState<CompanyVehicle[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVehicles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getCompanyVehicles(filters);
      setVehicles(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni a járműveket';
      setError(message);
      console.error('Error fetching company vehicles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters.type, filters.status, filters.search, filters.page, filters.pageSize]);

  useEffect(() => {
    void fetchVehicles();
  }, [fetchVehicles]);

  return { vehicles, total, isLoading, error, refetch: fetchVehicles };
}

/**
 * Hook to fetch a single vehicle by ID
 */
export function useVehicle(id: string | undefined): UseVehicleResult {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVehicle = useCallback(async () => {
    if (!id) {
      setVehicle(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getVehicleById(id);
      setVehicle(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült betölteni a járművet';
      setError(message);
      console.error('Error fetching vehicle:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchVehicle();
  }, [fetchVehicle]);

  return { vehicle, isLoading, error, refetch: fetchVehicle };
}

/**
 * Hook for vehicle mutations (create, update, delete)
 */
export function useVehicleMutations(): UseVehicleMutationsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrappedCreateRental: typeof createRentalVehicle = async (data: CreateRentalVehicleData) => {
    setIsLoading(true);
    setError(null);
    try {
      return await createRentalVehicle(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült létrehozni a járművet';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const wrappedCreateCompany: typeof createCompanyVehicle = async (
    data: CreateCompanyVehicleData
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      return await createCompanyVehicle(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült létrehozni a járművet';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const wrappedUpdate: typeof updateVehicle = async (id, data) => {
    setIsLoading(true);
    setError(null);
    try {
      return await updateVehicle(id, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült frissíteni a járművet';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const wrappedDelete: typeof deleteVehicle = async id => {
    setIsLoading(true);
    setError(null);
    try {
      return await deleteVehicle(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült törölni a járművet';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createRentalVehicle: wrappedCreateRental,
    createCompanyVehicle: wrappedCreateCompany,
    updateVehicle: wrappedUpdate,
    deleteVehicle: wrappedDelete,
    isLoading,
    error,
  };
}
